/**
 * Main App Component
 * 
 * This component contains the main application content with authentication
 * and permission-based navigation.
 */

import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Typography, 
  Box,
  Paper,
  Link,
  Grid,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Button,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Tooltip,
  Chip,
  Theme
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import SecurityIcon from '@mui/icons-material/Security';
import PermissionAwareTradeCodesView from './PermissionAwareTradeCodesView';
import PermissionAwareTechnologyTypesView from './PermissionAwareTechnologyTypesView';
import PermissionAwareSystemsView from './PermissionAwareSystemsView';
import PermissionFilteredValueChains from './PermissionFilteredValueChains';
import AdminDashboard from './admin/AdminDashboard';
import { AcceptInvitation } from './auth/AcceptInvitation';
import { Routes, Route, Navigate, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { posthog } from '../analytics/posthog';
import HeroBanner from './HeroBanner';
import { useAuth, AuthPage, ProtectedRoute } from './auth';
import AboutPage from './AboutPage';
import WebinarPage from './WebinarPage';
import { HomePageSEO, AboutSEO, TradeCodesSEO, TechnologyTypesSEO, SystemsSEO, CompaniesSEO, AuthSEO, GenericPageSEO } from './SEOWrapper';
import SEO from './SEO';

interface MainAppProps {}

const MainApp: React.FC<MainAppProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, isLoading, permissionsOverview } = useAuth();
  
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [dataMenuAnchor, setDataMenuAnchor] = useState<null | HTMLElement>(null);
  const [disclaimerOpen, setDisclaimerOpen] = useState<boolean>(false);
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [permDetailsOpen, setPermDetailsOpen] = useState<boolean>(false);

  // Lazy Companies view (feature is now always enabled)
  const CompaniesViewLazy = React.lazy(() => import('../features/companies/CompaniesView'));
  const DownstreamPotentialViewLazy = React.lazy(() => import('../features/downstream/DownstreamPotentialView'));

  useEffect(() => {
    if (posthog) {
      const path = location.pathname || '/trade-codes';
      const feature = path.startsWith('/systems') ? 'fullValueChain' : 
                     path.startsWith('/technology-types') ? 'hsCodeValueChain' :
                     path.startsWith('/companies') ? 'companies' : 'hsGroupedHSCode';
      posthog.capture('$pageview', { feature });
      posthog.capture('feature_viewed', { feature });
    }
  }, [location.pathname]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (menuAnchor) setMenuAnchor(null);
  };

  // Note: Avoid extra requests for value chain count; derive from permissions already in context

  const handleLogout = async () => {
    await logout();
    setUserMenuAnchor(null);
    navigate('/');
  };

  const handleLogin = () => {
    navigate('/auth');
    setUserMenuAnchor(null);
  };

  // If user is on auth route, show only AuthPage
  // This check must come BEFORE the isLoading check because AuthPage handles its own loading states
  // and we don't want to unmount it during auth operations (like forgotPassword)
  if (location.pathname === '/auth') {
    return (
      <AuthSEO>
        <AuthPage />
      </AuthSEO>
    );
  }

  // Show loading state (for non-auth routes)
  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(180deg, rgba(233, 233, 233, 0.08) 0%, rgba(233, 233, 233, 0.08) 55%, rgba(169, 255, 178, 1) 100%)'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2
        }}>
          <Box sx={{
            width: 40,
            height: 40,
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #4CAF50',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' }
            }
          }} />
          <Typography variant="h6" color="text.primary">
            Loading Productive Capabilities Data Lab...
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Please wait while we verify your access
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {/* Fixed background gradient (iOS Safari-safe) */}
      <Box aria-hidden sx={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        background: 'linear-gradient(180deg, rgba(233, 233, 233, 0.08) 0%, rgba(233, 233, 233, 0.08) 55%, rgba(169, 255, 178, 1) 100%)',
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover'
      }} />
      
      {/* Header */}
      <AppBar position="fixed" color="default" elevation={3} sx={{ 
        borderBottom: '1px solid', 
        borderColor: 'divider', 
        backgroundColor: '#fff' 
      }}>
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ gap: 2, minHeight: 64, py: 2.5 }}>
            <Link href="https://www.i3-t.org/" target="_blank" rel="noopener noreferrer" sx={{ lineHeight: 0, display: 'inline-flex' }}>
              <Box component="img" src="/i3t-logo.png" alt="i3T" sx={{ height: 36, display: 'block' }} />
            </Link>
            <Link href="https://rubidiumdata.com/" target="_blank" rel="noopener noreferrer" sx={{ lineHeight: 0, display: 'inline-flex' }}>
              <Box component="img" src="/RbD_logo.png" alt="RbD" sx={{ height: 36, display: 'block' }} />
            </Link>
            
            <Box sx={{ flexGrow: 1 }} />
            
            {/* Navigation Menu */}
            {!isMobile ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Button
                  variant="text"
                  onClick={() => navigate('/about')}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    textTransform: 'none',
                    '&:hover': { color: 'secondary.main', textDecoration: 'underline', backgroundColor: 'transparent' },
                    '&:active': { color: 'secondary.main' }
                  }}
                >
                  About
                </Button>
                <Button
                  variant="text"
                  onClick={(e) => setDataMenuAnchor(e.currentTarget)}
                  sx={{
                    color: 'text.primary',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    textTransform: 'none',
                    '&:hover': { color: 'secondary.main', textDecoration: 'underline', backgroundColor: 'transparent' },
                    '&:active': { color: 'secondary.main' }
                  }}
                >
                  Data
                </Button>
                <Menu
                  anchorEl={dataMenuAnchor}
                  open={Boolean(dataMenuAnchor)}
                  onClose={() => setDataMenuAnchor(null)}
                >
                  
                  <MenuItem
                    component={RouterLink}
                    to="/trade-codes"
                    onClick={() => setDataMenuAnchor(null)}
                  >
                    HS Codes (2nd aggregation)
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/technology-types"
                    onClick={() => setDataMenuAnchor(null)}
                  >
                    Technology Types (1st aggregation)
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/companies"
                    onClick={() => setDataMenuAnchor(null)}
                    disabled={!isAuthenticated}
                  >
                    {`Companies (1st aggregation)${!isAuthenticated ? ' (login required)' : ''}`}
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/downstream-potential"
                    onClick={() => setDataMenuAnchor(null)}
                    disabled={!isAuthenticated}
                  >
                    {`Downstream Potential (1st aggregation)${!isAuthenticated ? ' (login required)' : ''}`}
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/systems"
                    onClick={() => setDataMenuAnchor(null)}
                  >
                    Value Chain (no aggregation)
                  </MenuItem>
                  
                </Menu>
                
                {/* Admin Button - Only show if user has admin role */}
                {isAuthenticated && user?.role === 'admin' && (
                  <Button
                    variant="text"
                    component={RouterLink}
                    to="/admin"
                    sx={{
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      textTransform: 'none',
                      '&:hover': { color: 'secondary.main', textDecoration: 'underline', backgroundColor: 'transparent' },
                      '&:active': { color: 'secondary.main' }
                    }}
                  >
                    Admin
                  </Button>
                )}
                
                {/* User Menu */}
                {isAuthenticated ? (
                  <>
                    <Tooltip title={`Logged in as ${user?.full_name || user?.email}`}>
                      <IconButton
                        onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                        sx={{ ml: 1 }}
                      >
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                          {user?.full_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
                        </Avatar>
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={userMenuAnchor}
                      open={Boolean(userMenuAnchor)}
                      onClose={() => setUserMenuAnchor(null)}
                    >
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">
                          {user?.full_name || user?.email}
                        </Typography>
                      </MenuItem>
                      <MenuItem onClick={handleLogout}>
                        <LogoutIcon sx={{ mr: 1 }} />
                        Logout
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLogin}
                    sx={{ ml: 1 }}
                  >
                    Login
                  </Button>
                )}
              </Box>
            ) : (
              <>
                <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="menu" color="inherit" sx={{ ml: 1 }}>
                  <MenuIcon />
                </IconButton>
                <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
                  <MenuItem component={RouterLink} to="/about" onClick={() => setMenuAnchor(null)}>About</MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/systems"
                    onClick={() => setMenuAnchor(null)}
                  >
                    Value Chain (no aggregation)
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/technology-types"
                    onClick={() => setMenuAnchor(null)}
                  >
                    Technology Types (first level aggregation)
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/companies"
                    onClick={() => setMenuAnchor(null)}
                    disabled={!isAuthenticated}
                  >
                    {`Companies (first level aggregation)${!isAuthenticated ? ' (login required)' : ''}`}
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/downstream-potential"
                    onClick={() => setMenuAnchor(null)}
                    disabled={!isAuthenticated}
                  >
                    {`Downstream Potential (first level aggregation)${!isAuthenticated ? ' (login required)' : ''}`}
                  </MenuItem>
                  <MenuItem
                    component={RouterLink}
                    to="/trade-codes"
                    onClick={() => setMenuAnchor(null)}
                  >
                    HS Codes (second aggregation)
                  </MenuItem>
                  {isAuthenticated ? (
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  ) : (
                    <MenuItem onClick={handleLogin}>
                      <LoginIcon sx={{ mr: 1 }} />
                      Login
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>

      {/* Offset for fixed AppBar */}
      <Box sx={{ height: { xs: 80, md: 88 } }} />

      <Container maxWidth="lg" id="top">
        <Box sx={{ my: 4 }}>
          {!(isMobile && location.pathname === '/webinar') && <HeroBanner />}

          {/* About/Approach moved to dedicated /about route */}

          {/* Logged-in permissions summary */}
          {isAuthenticated && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(16,185,129,0.12)' : 'rgba(76, 175, 80, 0.1)', border: '1px solid', borderColor: 'success.main' }}>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'row' }, alignItems: 'center', flexWrap: 'wrap', rowGap: 1, columnGap: 1.5, mb: 1 }}>
                <SecurityIcon color="success" />
                <Typography
                  variant="subtitle2"
                  color="success.dark"
                  component="span"
                  sx={{ whiteSpace: { xs: 'normal', md: 'nowrap' } }}
                >
                  {(() => {
                    const fn = (user?.first_name || '').trim();
                    const ln = (user?.last_name || '').trim();
                    const name =
                      [fn, ln].filter(Boolean).join(' ') ||
                      (user?.full_name ? String(user.full_name).trim() : '') ||
                      (user?.email ? String(user.email).trim() : '');
                    return name ? `Hi ${name}, you are logged in` : 'You are logged in';
                  })()}.
                </Typography>
                {isMobile && (
                  <Button size="small" variant="text" onClick={() => setPermDetailsOpen(v => !v)} sx={{ ml: 0.5 }}>
                    {permDetailsOpen ? 'Hide details' : 'Show details'}
                  </Button>
                )}
                {(!isMobile || permDetailsOpen) && (() => {
                  const overviewCount = typeof (permissionsOverview as any)?.value_chains_count === 'number' ? (permissionsOverview as any).value_chains_count : 0;
                  const companies = Array.isArray((permissionsOverview as any)?.company_mapping_countries) ? (permissionsOverview as any).company_mapping_countries : [];
                  const financial = Array.isArray((permissionsOverview as any)?.financial_data_access) ? (permissionsOverview as any).financial_data_access : [];
                  return (
                    <>
                      {overviewCount > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            additional value chains:
                          </Typography>
                          <Chip label={String(overviewCount)} size="small" variant="outlined" />
                        </Box>
                      )}
                      {companies.length > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            company mapping for:
                          </Typography>
                          <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {companies.map((name, idx) => (
                              <Chip key={`${name}-${idx}`} label={name} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                      {financial.length > 0 && (
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                          <Typography variant="body2" color="text.secondary" component="span">
                            financial data:
                          </Typography>
                          <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {financial.map((it: any, idx: number) => {
                              const label = `${String(it?.country || '').trim()}${it?.source ? ` • ${String(it.source).trim()}` : ''}`;
                              return <Chip key={`${it.country}-${it.source}-${idx}`} label={label} size="small" variant="outlined" />;
                            })}
                          </Box>
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Paper>
          )}

          {/* Data Access Notice */}
          {!isAuthenticated && location.pathname !== '/webinar' && (
            <Paper elevation={1} sx={{ p: 2, mb: 2, bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(245,158,11,0.12)' : 'rgba(255, 193, 7, 0.1)', border: '1px solid', borderColor: 'warning.main' }}>
              <Typography variant="body2" color="text.secondary" align="center">
                You are not logged in. To gain access to additional value chains, company data, and more, please{' '}
                <Link component={RouterLink} to="/auth">sign in</Link> or contact <Link href="mailto:support@rubidiumdata.com">support@rubidiumdata.com</Link>.
              </Typography>
            </Paper>
          )}

          {/* Feature Content via routes */}
          <Box id="graphs" />
          <Routes>
            {/* ABOUT PAGE */}
            <Route path="/about" element={<AboutSEO><AboutPage /></AboutSEO>} />
            {/* WEBINAR PAGE */}
            <Route
              path="/webinar"
              element={
                <GenericPageSEO
                  title="Webinar"
                  description="Webinar information and resources for the Productive Capabilities Data Lab."
                >
                  <WebinarPage />
                </GenericPageSEO>
              }
            />
            {/* PUBLIC GRAPH PAGES - No authentication required */}
            <Route path="/trade-codes" element={<TradeCodesSEO><PermissionAwareTradeCodesView /></TradeCodesSEO>} />
            <Route path="/technology-types" element={<TechnologyTypesSEO><PermissionAwareTechnologyTypesView /></TechnologyTypesSEO>} />
            <Route path="/systems" element={<SystemsSEO><PermissionAwareSystemsView /></SystemsSEO>} />
            <Route
              path="/companies"
              element={
                <CompaniesSEO>
                  <React.Suspense fallback={null}>
                    <CompaniesViewLazy />
                  </React.Suspense>
                </CompaniesSEO>
              }
            />
            <Route
              path="/downstream-potential"
              element={
                <GenericPageSEO
                  title="Downstream Potential"
                  description="Explore downstream potential across value chains."
                >
                  <React.Suspense fallback={null}>
                    <DownstreamPotentialViewLazy />
                  </React.Suspense>
                </GenericPageSEO>
              }
            />
            
            {/* PROTECTED PAGES - Keep authentication */}
            <Route path="/permissions" element={
              <>
                <SEO
                  title="Permissions"
                  description="Account permissions overview."
                  canonical={`https://valuechains.ai${location.pathname}`}
                  noindex
                />
                <ProtectedRoute requireAuth={true}>
                  <PermissionFilteredValueChains />
                </ProtectedRoute>
              </>
            } />
            <Route path="/admin" element={
              <>
                <SEO
                  title="Admin"
                  description="Administration dashboard."
                  canonical={`https://valuechains.ai${location.pathname}`}
                  noindex
                />
                <ProtectedRoute requiredPermissions={['admin']}>
                  <AdminDashboard />
                </ProtectedRoute>
              </>
            } />
            <Route
              path="/accept-invitation"
              element={
                <>
                  <SEO
                    title="Accept invitation"
                    description="Accept your invitation to Productive Capabilities Data Lab."
                    canonical={`https://valuechains.ai${location.pathname}`}
                    noindex
                  />
                  <AcceptInvitation />
                </>
              }
            />
            <Route path="/" element={<HomePageSEO><AboutPage /></HomePageSEO>} />
            <Route path="*" element={<Navigate to="/about" replace />} />
          </Routes>
        </Box>

        {/* Footer */}
        <Box component="footer" sx={{ mt: 6, pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}>
          <Box sx={{
            width: '85vw',
            position: 'relative',
            left: '50%',
            transform: 'translateX(-50%)',
            borderTop: '1px solid #000',
            mb: { xs: 3, md: 4 }
          }} />
          <Grid container spacing={4} justifyContent="space-between" alignItems="flex-start">
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box component="img" src="/i3t_logo_black.png" alt="i3T" sx={{ height: 34, display: 'block' }} />
                <Box component="img" src="/RbD_logo_black.png" alt="RbD" sx={{ height: 34, display: 'block' }} />
              </Box>
              <Typography variant="body2" color="text.primary" sx={{ mb: 0.5 }}>
                © i3T & RubidiumData
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, maxWidth: 720 }}>
                Data is experimental and subject to refinement. External links are provided for illustrative and reference purposes only; we are not responsible for the content of third-party websites.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} lg={3}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
                Site Map
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Link component="button" variant="body2" onClick={() => scrollToId('top')} sx={{ color: 'text.primary', textAlign: 'left' }}>Home</Link>                
                <Link href="https://www.i3-t.org/" target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" sx={{ color: 'text.primary' }}>i3T</Link>
                <Link href="https://rubidiumdata.com/" target="_blank" rel="noopener noreferrer" underline="hover" variant="body2" sx={{ color: 'text.primary' }}>RubidiumData</Link>
                <Link component="button" variant="body2" onClick={() => setDisclaimerOpen(true)} sx={{ color: 'text.primary', textAlign: 'left' }}>Disclaimer</Link>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Disclaimer Dialog */}
        <Dialog open={disclaimerOpen} onClose={() => setDisclaimerOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Disclaimer</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" paragraph>
              The information presented on this platform, including value chain data and related analyses, is generated using AI-based methods and is provided for general informational purposes only. While we make every effort to compile data accurately, the information is experimental, may be incomplete or outdated, and should not be relied upon as a sole source for decision-making.
            </Typography>
            <Typography variant="body2" paragraph>
              This platform may contain links to external websites and resources. These links are provided for illustrative and reference purposes only. We do not endorse, control, or assume responsibility for the content, accuracy, or reliability of any third-party websites. Accessing such external resources is at the user's own risk.
            </Typography>
            <Typography variant="body2">
              To the fullest extent permitted by law, we disclaim any liability for errors, omissions, or outcomes arising from the use of this platform or reliance on its content. Users are encouraged to independently verify information before acting on it.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDisclaimerOpen(false)} variant="contained">Close</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default MainApp;
